import { useLayoutEffect, useRef } from "react";
import birdFlight from "../../../images/royal-mahal/bird-flight.webp";
import { FRAMES, createBird, facingOf, frameOf, launch, step } from "../art/flight";
import { prefersReducedMotion } from "../hooks";

const ASPECT = 122 / 128; // sprite frame height / width
const LOADER_BEAT = 1 / 0.62; // wingbeats per second in the loader
const SETTLE_S = 2.6; // time to ease from the loader's hover into ordinary flight
const LEAVE_AT_S = 4.2; // after this long about the gate, the bird climbs away out of the frame
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const smooth = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;

/**
 * The loader's bird, carried on into the gate. It appears exactly where, and at the same
 * size and wing position as, the loader's bird, then takes off: wingbeats quicken to the
 * usual pace, it eases down to the size of the birds in the sky and flies about the gate
 * under the same flight model (art/flight.js). After a few seconds it climbs away and
 * flies out of the frame.
 */
const GateBird = ({ from }) => {
  const el = useRef(null);

  useLayoutEffect(() => {
    const node = el.current;
    if (!node || !from || prefersReducedMotion()) return undefined;
    const sprite = node.firstChild;
    const area = { w: 0, h: 0 };
    let top = 0;
    const measure = () => {
      const vh = window.innerHeight;
      area.w = window.innerWidth;
      top = vh * 0.05;
      area.h = Math.max(160, vh * 0.66);
    };
    measure();

    const endL = clamp(area.w * 0.05, 46, 66);
    const bird = createBird(from.w);
    launch(bird, area, { dir: 1, x: from.x, y: from.y - top });
    // Exactly where the loader's bird was, moving gently, wings where they were
    bird.y = from.y - top;
    bird.yTarget = clamp(bird.y, 0.3 * area.h, 0.75 * area.h);
    bird.vx = 0.5 * bird.U * bird.L;
    bird.vy = 0;
    bird.phase = from.frame;
    const cruiseBeat = bird.f0;

    const draw = (age) => {
      const settle = smooth(clamp(age / SETTLE_S, 0, 1));
      const scale = lerp(1, facingOf(bird), smooth(clamp(age / 1.5, 0, 1)));
      node.style.opacity = "1";
      node.style.width = `${bird.L.toFixed(1)}px`;
      node.style.transform = `translate3d(${(bird.x - bird.L / 2).toFixed(1)}px, ${(top + bird.y - (bird.L * ASPECT) / 2).toFixed(1)}px, 0)`;
      sprite.style.transform = `scaleX(${scale.toFixed(3)}) rotate(${(bird.pitch * settle).toFixed(3)}rad)`;
      sprite.style.backgroundPositionX = `${(frameOf(bird) / (FRAMES - 1)) * 100}%`;
    };
    draw(0);

    let age = 0;
    let last = 0;
    let frame = requestAnimationFrame(function tick(now) {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
      last = now;
      age += dt;
      const settle = smooth(clamp(age / SETTLE_S, 0, 1));
      bird.L = lerp(from.w, endL, settle);
      bird.f0 = lerp(LOADER_BEAT, cruiseBeat, settle);
      if (age > LEAVE_AT_S && !bird.exiting) {
        // Head off the way it is already flying, climbing as it goes
        bird.exiting = true;
        bird.turnAt = null;
        bird.yTarget = -0.6 * area.h;
      }
      step(bird, dt, area);
      draw(age);
      const m = bird.L * 1.2;
      if (bird.exiting && (bird.x < -m || bird.x > area.w + m || top + bird.y < -m)) {
        node.style.opacity = "0"; // out of the frame: done
        return;
      }
      frame = requestAnimationFrame(tick);
    });

    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
    };
  }, [from]);

  if (!from) return null;
  return (
    <div ref={el} className="rm-flyer rm-gate__bird" aria-hidden="true">
      <div className="rm-flyer__sprite" style={{ backgroundImage: `url(${birdFlight})` }} />
    </div>
  );
};

export default GateBird;
